import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * How many API requests are in flight, reduced to "is anything happening".
 *
 * Feeds the thin progress ribbon in the page layout. Deliberately a count
 * rather than a boolean: a page load fires half a dozen requests at once, and
 * the ribbon has to stay up until the last one lands rather than dropping on
 * the first.
 *
 * `providedIn: 'root'` on purpose. HttpClientWithInFlightCache is provided by
 * its own NgModule, so there can be several instances depending on which
 * modules import it; the count has to be one shared number across all of them
 * or the ribbon would reflect whichever instance happened to be asked.
 */
@Injectable({ providedIn: 'root' })
export class NetworkActivityService {
    private readonly count = new BehaviorSubject<number>(0);

    /**
     * True while at least one request is outstanding.
     *
     * distinctUntilChanged so back-to-back requests do not flicker the ribbon
     * off and on between them, which reads worse than no ribbon at all.
     */
    readonly busy$: Observable<boolean> = this.count.pipe(
        map((n) => n > 0),
        distinctUntilChanged()
    );

    started(): void {
        this.count.next(this.count.value + 1);
    }

    finished(): void {
        // Clamped: an unbalanced finish would drive the count negative, after
        // which the next genuine request would not register as busy at all.
        this.count.next(Math.max(0, this.count.value - 1));
    }
}
